import { lazy, Suspense, type FC } from 'react'
import { Routes, Route } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { AuthGuard, UnauthGuard } from './components/Auth'
import { ErrorBoundary } from './components/ErrorBoundary'

// Lazy-loaded pages (loaded on-demand for better initial page load).
//
// These resolve fine under the static prerender too: React 19's
// `react-dom/static` prerender waits for every Suspense boundary to settle
// before it resolves, rather than emitting the fallback the way
// `renderToString` would. That is the whole reason the SSG entry uses it.
const LandingPage = lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage })))
const AuthPage = lazy(() => import('./pages/AuthPage').then(m => ({ default: m.AuthPage })))
const ChatPage = lazy(() => import('./pages/ChatPage').then(m => ({ default: m.ChatPage })))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const PromptTestingPage = lazy(() => import('./pages/PromptTestingPage').then(m => ({ default: m.PromptTestingPage })))
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })))
const EmailVerificationPage = lazy(() => import('./pages/EmailVerificationPage').then(m => ({ default: m.EmailVerificationPage })))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })))
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })))
const DoraPage = lazy(() => import('./pages/DoraPage').then(m => ({ default: m.DoraPage })))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))

/**
 * PageLoader - Loading fallback for lazy-loaded pages
 */
const PageLoader: FC = () => (
  <div className="flex items-center justify-center h-screen bg-sand-100 dark:bg-[#1A1A1A]">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="w-8 h-8 text-accent-main animate-spin" />
      <p className="text-sm text-sand-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
)

/**
 * AppRoutes - the route table, router-agnostic.
 *
 * Extracted from `App.tsx` so it can be mounted under either router: a
 * `BrowserRouter` in the browser (see `App.tsx`) or a `StaticRouter` during the
 * build-time prerender (see `entry-ssg.tsx`). Nothing here may reference
 * `window`, `document` or storage at render time — the prerender runs in Node
 * with no DOM, and a violation surfaces as a build failure, not a runtime one.
 */
export const AppRoutes: FC = () => (
  <Routes>
    {/* Root route - Landing page for unauth, redirect to chat for auth */}
    <Route
      path="/"
      element={
        <ErrorBoundary>
          <UnauthGuard redirectTo="/chat">
            <Suspense fallback={<PageLoader />}>
              <LandingPage />
            </Suspense>
          </UnauthGuard>
        </ErrorBoundary>
      }
    />

    {/* Auth route (login/signup) */}
    <Route path="/auth" element={
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <AuthPage />
        </Suspense>
      </ErrorBoundary>
    } />

    {/* Email verification route */}
    <Route path="/auth/verify-email" element={
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <EmailVerificationPage />
        </Suspense>
      </ErrorBoundary>
    } />

    {/* Protected chat route */}
    <Route
      path="/chat"
      element={
        <ErrorBoundary>
          <AuthGuard>
            <Suspense fallback={<PageLoader />}>
              <ChatPage />
            </Suspense>
          </AuthGuard>
        </ErrorBoundary>
      }
    />

    {/* Protected settings route */}
    <Route
      path="/settings"
      element={
        <ErrorBoundary>
          <AuthGuard>
            <Suspense fallback={<PageLoader />}>
              <SettingsPage />
            </Suspense>
          </AuthGuard>
        </ErrorBoundary>
      }
    />

    {/* Pricing page - accessible to all users (comparison table for landing page visitors) */}
    <Route
      path="/pricing"
      element={
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <PricingPage />
          </Suspense>
        </ErrorBoundary>
      }
    />

    {/* Protected prompt testing route */}
    <Route
      path="/prompt-testing"
      element={
        <ErrorBoundary>
          <AuthGuard>
            <Suspense fallback={<PageLoader />}>
              <PromptTestingPage />
            </Suspense>
          </AuthGuard>
        </ErrorBoundary>
      }
    />

    {/* Admin DORA metrics dashboard */}
    <Route
      path="/admin/dora"
      element={
        <ErrorBoundary>
          <AuthGuard>
            <Suspense fallback={<PageLoader />}>
              <DoraPage />
            </Suspense>
          </AuthGuard>
        </ErrorBoundary>
      }
    />

    {/* Public legal pages (no auth required) */}
    <Route
      path="/privacy"
      element={
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <PrivacyPage />
          </Suspense>
        </ErrorBoundary>
      }
    />
    <Route
      path="/terms"
      element={
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <TermsPage />
          </Suspense>
        </ErrorBoundary>
      }
    />

    {/* 404 - Not Found */}
    <Route path="*" element={
      <Suspense fallback={<PageLoader />}>
        <NotFoundPage />
      </Suspense>
    } />
  </Routes>
)
